import { useMutation } from '@apollo/client';
import { AuthErrorCode } from '@common/neo.tax';
import { Stack, Typography } from '@ui-components/neo.tax';
import { useState } from 'react';
import z from 'zod';

import { useRedirectIfEmailVerified } from '@neo.tax/onboarding/hooks';
import {
  TestimonialMemo,
  StyledSplitScreen,
  AuthFormHeader,
  StyledGeneralFormError,
  StyledLinkText,
  SubmitButton,
} from '@neo.tax/onboarding/organisms';
import {
  Loader,
  NavLinkTo,
  Path,
  SEND_EMAIL_VERIFICATION,
  SendEmailVerificationMutation,
  SendEmailVerificationMutationVariables,
  getPath,
  useUrlQueryParam,
} from '@neo.tax/shared';
import { useUserFromAuth } from '@neo.tax/shared/hooks/useUserFromAuth';

type CopyVariant = {
  title: string;
  description: string;
  resendLabel: string;
  resentLabel: string;
};

const DEFAULT_COPY: CopyVariant = {
  title: 'Check your email',
  description: 'We sent a verification link to your email. Click it to finish setting up your account.',
  resendLabel: 'Resend email',
  resentLabel: 'Email sent',
};

const SECURITY_UPDATE_COPY: CopyVariant = {
  title: 'Verify your email to continue',
  description:
    "We've updated our security policy — please verify your email address to continue. Click below and we'll send you a verification link.",
  resendLabel: 'Send verification email',
  resentLabel: 'Email sent',
};

const EXPIRED_COPY: CopyVariant = {
  title: 'That verification link expired',
  description: "Click below and we'll send you a fresh one.",
  resendLabel: 'Send new link',
  resentLabel: 'Email sent',
};

const getCopy = (reason: string | null | undefined): CopyVariant => {
  if (reason === 'security-update') return SECURITY_UPDATE_COPY;
  if (reason === 'expired') return EXPIRED_COPY;
  return DEFAULT_COPY;
};

// Apollo client wraps GraphQL errors in a graphQLErrors array; our
// ExtendedGraphQLErrors puts the interesting `code` at a specific depth.
// Parse with zod so the shape is validated at runtime and the extracted
// code is typed correctly without casting.
const apolloErrorShape = z.object({
  graphQLErrors: z
    .array(
      z.object({
        extensions: z
          .object({
            errors: z.array(z.object({ code: z.string() })).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});

const getAuthErrorCode = (error: unknown): string | undefined => {
  const parsed = apolloErrorShape.safeParse(error);
  if (!parsed.success) {
    return undefined;
  }
  return parsed.data.graphQLErrors?.[0]?.extensions?.errors?.[0]?.code;
};

export const VerifyEmailSent = (): React.ReactElement => {
  const reason = useUrlQueryParam('reason');
  const continueUrl = useUrlQueryParam('continueUrl');
  const copy = getCopy(reason);

  const { userId } = useUserFromAuth();

  const [sendEmailVerification] = useMutation<SendEmailVerificationMutation, SendEmailVerificationMutationVariables>(
    SEND_EMAIL_VERIFICATION,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onResend = async (): Promise<void> => {
    if (!userId) {
      setErrorMessage('You need to sign in before we can send a verification email.');
      return;
    }
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await sendEmailVerification({ variables: { userId, continueUrl: continueUrl ?? undefined } });
      setHasSent(true);
    } catch (error) {
      const code = getAuthErrorCode(error);
      if (code === AuthErrorCode.EMAIL_VERIFICATION_TOO_FREQUENT) {
        setErrorMessage('A verification email was sent recently. Please wait a moment before trying again.');
      } else if (code === AuthErrorCode.EMAIL_VERIFICATION_TOO_MANY_ATTEMPTS_TRY_LATER) {
        setErrorMessage('Too many verification attempts. Please try again in a few minutes.');
      } else {
        setErrorMessage("We couldn't send the verification email. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Loader useRedirect={useRedirectIfEmailVerified}>
      <StyledSplitScreen
        leftSide={
          <AuthFormHeader
            pageChangeText="Already verified?"
            pageChangeLinkText="Sign in"
            pageChangeTo="/login"
            title={copy.title}
            description={copy.description}
            form={
              <Stack direction="column" paddingY={6}>
                <SubmitButton
                  type="button"
                  variant="primary"
                  rounded="regular"
                  size="base"
                  disabled={isSubmitting || hasSent || !userId}
                  onClick={onResend}
                >
                  {hasSent ? copy.resentLabel : copy.resendLabel}
                </SubmitButton>
                {!userId ? (
                  <Typography as="span" fontSize="sm" fontWeight="normal">
                    You need to{' '}
                    <NavLinkTo to={getPath({ path: Path.LOGIN })}>
                      <StyledLinkText as="span" fontSize="sm" fontWeight="medium">
                        sign in
                      </StyledLinkText>
                    </NavLinkTo>{' '}
                    before we can send a new verification email.
                  </Typography>
                ) : null}
                <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
              </Stack>
            }
          />
        }
        rightSide={<TestimonialMemo />}
      />
    </Loader>
  );
};
